/* eslint-disable react-hooks/rules-of-hooks -- pre-existing violations unmasked by #1772; tracked + to be fixed in #1842. Do not add new conditional hooks here. */
import { useList, useOne, useUpdate, useCustomMutation } from '@refinedev/core'
import { useForm, Controller } from 'react-hook-form'
import {
  Box,
  Typography,
  Button,
  FormControl,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  Alert,
  Link,
  Chip,
  CircularProgress,
} from '@mui/material'
import { useState, useEffect, useRef } from 'react'
import { UUID } from 'crypto'
import { set, format } from 'date-fns'
import OrgChargesPayments from './OrgChargesPayments'
import LaunchIcon from '@mui/icons-material/Launch'
import SyncIcon from '@mui/icons-material/Sync'
import WarningIcon from '@mui/icons-material/Warning'
import {
  OrgAccountData,
  XeroInvoiceSetupResponse,
  XeroSyncResponse,
} from '../interfaces/accountsInterfaces'
import { getTierLabel, getTierPricingLabel } from '../utils/tierUtils'

interface OrgAccountManagementProps {
  orgUuid: UUID
  parentOrgName: string | null
  hasSubOrgs: boolean
}

interface OrgAccountFormData {
  org_uuid: UUID
  membership?: string
  membership_until?: string
  tier?: string | number
  payment_type?: string
  billing_frequency?: string
  billing_amount?: number | null
  pricing?: Pricing
  billing_legal_name?: string | null
  billing_email?: string | null
  billing_contact_name?: string | null
  billing_contact_phone?: string | null
  billing_address_line1?: string | null
  billing_address_line2?: string | null
  billing_city?: string | null
  billing_state?: string | null
  billing_postal_code?: string | null
  billing_country?: string | null
  tax_registration_number?: string | null
  billing_notes?: string | null
}

type Pricing = 'tier' | 'custom' | 'sponsored'

// orgs.billing_amount: NULL charges the tier price, 0 is sponsored, anything
// else is a custom price per billing cycle.
const pricingFor = (amount: number | string | null | undefined): Pricing => {
  if (amount == null) return 'tier'
  return Number(amount) === 0 ? 'sponsored' : 'custom'
}

const XERO_REQUIRED_BILLING_FIELDS = [
  { key: 'billing_email', label: 'billing email' },
  { key: 'billing_address_line1', label: 'billing address line 1' },
  { key: 'billing_city', label: 'billing city' },
  { key: 'billing_postal_code', label: 'billing postal code' },
  { key: 'billing_country', label: 'billing country' },
] as const

type XeroRequiredBillingField =
  (typeof XERO_REQUIRED_BILLING_FIELDS)[number]['key']

const isBlank = (value: unknown) =>
  typeof value !== 'string' || value.trim().length === 0

const getBillingSaveErrorMessage = (error: any) => {
  const detail = error?.response?.data?.detail
  if (typeof detail === 'string' && detail.trim()) {
    return detail
  }

  const message = error?.message
  if (typeof message === 'string' && message.trim()) {
    return message
  }

  return 'Failed to save billing details'
}

export default function OrgAccountManagement({
  orgUuid,
  parentOrgName,
  hasSubOrgs,
}: OrgAccountManagementProps) {
  const [isDialogOpen, setIsDialogOpen] = useState(false)
  const [pricing, setPricing] = useState<Pricing>('tier')
  const [setupXeroAfterBillingSave, setSetupXeroAfterBillingSave] =
    useState(false)
  const [saving, setSaving] = useState(false)
  const [billingSaveError, setBillingSaveError] = useState<string | null>(null)
  const billingSaveAttemptRef = useRef(0)

  // Early return if no orgUuid provided
  if (!orgUuid) {
    return (
      <Box sx={{ display: 'flex', gap: 2 }}>
        <Typography variant="body1" color="textSecondary">
          No organisation ID provided - cannot load billing information.
        </Typography>
      </Box>
    )
  }

  const { data, isLoading, isError, refetch: refetchAccount } =
    useOne<OrgAccountData>({
      resource: 'account',
      id: orgUuid,
      queryOptions: {
        enabled: !!orgUuid, // Only run query if orgUuid is provided
      },
    })
  const { mutate: update } = useUpdate()
  const { mutate: syncXeroBilling, isLoading: syncingXeroBilling } =
    useCustomMutation<XeroSyncResponse>()
  const { mutate: setupXeroInvoicing, isLoading: settingUpXeroInvoicing } =
    useCustomMutation<XeroInvoiceSetupResponse>()
  const [syncResult, setSyncResult] = useState<XeroSyncResponse | null>(null)
  const [xeroSetupResult, setXeroSetupResult] =
    useState<XeroInvoiceSetupResponse | null>(null)

  const handleSyncXeroBilling = () => {
    setSyncResult(null)
    syncXeroBilling(
      {
        url: `/xero/sync/org/${orgUuid}`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: (response) => {
          setSyncResult(response.data)
        },
        onError: (error: any) => {
          setSyncResult({
            success: false,
            message:
              error?.response?.data?.detail ||
              error?.message ||
              'Sync failed — check logs',
          })
        },
      }
    )
  }

  const runSetupXeroInvoicing = () => {
    setXeroSetupResult(null)
    setupXeroInvoicing(
      {
        url: `/orgs/account/${orgUuid}/setup-xero-invoicing`,
        method: 'post',
        values: {},
      },
      {
        onSuccess: (response) => {
          setXeroSetupResult(response.data)
          refetchAccount()
        },
        onError: (error: any) => {
          setXeroSetupResult({
            success: false,
            message:
              error?.response?.data?.detail ||
              error?.message ||
              'Xero setup failed — check logs',
          })
        },
      }
    )
  }

  const getMissingXeroBillingFields = (account?: OrgAccountData) =>
    XERO_REQUIRED_BILLING_FIELDS.filter(({ key }) => isBlank(account?.[key]))

  const handleSetupXeroInvoicing = () => {
    const missingBillingFields = getMissingXeroBillingFields(data?.data)

    if (missingBillingFields.length > 0) {
      setXeroSetupResult({
        success: false,
        message: `Enter ${missingBillingFields
          .map(({ label }) => label)
          .join(', ')} to set up Xero invoicing.`,
      })
      handleOpenDialog(true)
      return
    }

    runSetupXeroInvoicing()
  }

  const { data: orgChildrenData, isLoading: orgChildrenDataIsLoading } =
    useList({
      resource: 'org-children',
      meta: {
        org_uuid: orgUuid,
      },
      queryOptions: {
        enabled: !!orgUuid && hasSubOrgs,
      },
      errorNotification: (error) => {
        return {
          message: 'Error getting organisation child orgs',
          description:
            error?.message || 'An error occurred while fetching child orgs',
          type: 'error',
        }
      },
    })

  // Fetch discount information for this organisation
  const { data: orgDiscountData } = useOne({
    resource: 'discount-codes/orgs',
    id: `${orgUuid}/discount`,
    queryOptions: {
      enabled: !!orgUuid, // Only run query if orgUuid is provided
    },
  })

  // The discount data for this organisation
  const orgWithDiscount = orgDiscountData?.data

  const {
    handleSubmit,
    control,
    reset,
    formState: { errors },
    watch,
    setError,
    getValues,
  } = useForm<OrgAccountFormData>({
    mode: 'onChange',
  })

  const watchPaymentType = watch('payment_type')
  const watchTier = watch('tier')
  // A billing amount of 0 (sponsored or a 100% discount) is invoiced nothing,
  // so once its Xero contact is linked there is nothing left to set up.
  const isSponsored =
    data?.data.billing_amount != null && Number(data.data.billing_amount) === 0
  const hasMissingSubscription =
    !!data?.data &&
    !data.data.stripe_subscription_id &&
    !data.data.xero_repeating_invoice_id &&
    !(isSponsored && data.data.xero_contact_id)
  const canSetupXeroInvoicing =
    hasMissingSubscription && data?.data.payment_type?.toLowerCase() === 'invoice'

  function onSubmit(data: OrgAccountFormData) {
    const billingSaveAttempt = ++billingSaveAttemptRef.current
    setBillingSaveError(null)

    if (setupXeroAfterBillingSave) {
      const missingFields = XERO_REQUIRED_BILLING_FIELDS.filter(({ key }) =>
        isBlank(data[key])
      )

      if (missingFields.length > 0) {
        missingFields.forEach(({ key, label }) => {
          setError(key as XeroRequiredBillingField, {
            type: 'required',
            message: `${label} is required to set up Xero invoicing`,
          })
        })
        setXeroSetupResult({
          success: false,
          message: `Enter ${missingFields
            .map(({ label }) => label)
            .join(', ')} to set up Xero invoicing.`,
        })
        return
      }
    }

    // Only auto-set membership to 'Paid' for Invoice payment type if membership isn't already set
    if (
      data.payment_type === 'Invoice' &&
      (!data.membership || data.membership === '')
    ) {
      data.membership = 'Paid'
    }

    // Ensure billing frequency is included (allow empty string for null)
    // Sometimes react-hook-form doesn't properly include all field values
    if (data.billing_frequency === undefined) {
      data.billing_frequency = getValues('billing_frequency') || ''
    }

    // Convert tier to number (backend expects integer)
    if (data.tier !== undefined && data.tier !== null && data.tier !== '') {
      data.tier = parseInt(data.tier as string, 10)
    }

    // Stripe prices live in Stripe; for invoice orgs the price is explicit,
    // because leaving the amount out can't tell the backend to clear it.
    if (data.payment_type === 'Stripe') {
      delete data.billing_amount
    } else {
      data.pricing = pricing
      if (pricing !== 'custom') {
        data.billing_amount = null
      } else if (
        data.billing_amount == null ||
        String(data.billing_amount) === '' ||
        Number(data.billing_amount) <= 0
      ) {
        setError('billing_amount', {
          type: 'required',
          message: 'Enter a price above $0, or choose Sponsored',
        })
        return
      }
    }

    setSaving(true)

    update(
      {
        resource: 'account',
        id: orgUuid,
        values: { ...data },
      },
      {
        onError: (error) => {
          if (billingSaveAttempt !== billingSaveAttemptRef.current) return
          setSaving(false)
          setBillingSaveError(getBillingSaveErrorMessage(error))
        },
        onSuccess: () => {
          const shouldSetupXero = setupXeroAfterBillingSave
          if (billingSaveAttempt === billingSaveAttemptRef.current) {
            setSaving(false)
            setBillingSaveError(null)
            handleCloseDialog()
          }
          refetchAccount()
          if (shouldSetupXero) {
            runSetupXeroInvoicing()
          }
        },
      }
    )
  }

  const handleOpenDialog = (setupXeroAfterSave = false) => {
    billingSaveAttemptRef.current += 1
    setSetupXeroAfterBillingSave(setupXeroAfterSave)
    setBillingSaveError(null)
    setPricing(pricingFor(data?.data.billing_amount))
    reset({
      org_uuid: orgUuid,
      membership: data?.data.membership,
      membership_until: data?.data.membership_until,
      tier:
        data?.data.tier !== null && data?.data.tier !== undefined
          ? data.data.tier.toString()
          : '-1',
      payment_type: data?.data.payment_type || '',
      billing_frequency: data?.data.billing_frequency || '',
      billing_amount: data?.data.billing_amount ?? null,
      billing_legal_name: data?.data.billing_legal_name || '',
      billing_email: data?.data.billing_email || '',
      billing_contact_name: data?.data.billing_contact_name || '',
      billing_contact_phone: data?.data.billing_contact_phone || '',
      billing_address_line1: data?.data.billing_address_line1 || '',
      billing_address_line2: data?.data.billing_address_line2 || '',
      billing_city: data?.data.billing_city || '',
      billing_state: data?.data.billing_state || '',
      billing_postal_code: data?.data.billing_postal_code || '',
      billing_country: data?.data.billing_country || 'New Zealand',
      tax_registration_number: data?.data.tax_registration_number || '',
      billing_notes: data?.data.billing_notes || '',
    })
    setIsDialogOpen(true)
  }

  const handleCloseDialog = () => {
    billingSaveAttemptRef.current += 1
    setIsDialogOpen(false)
    setSetupXeroAfterBillingSave(false)
    setSaving(false)
    setBillingSaveError(null)
    reset()
  }

  return (
    <Box sx={{ display: 'flex', gap: 2 }}>
      {/* Left column - Organisation Accounts Data */}
      <Box sx={{ flex: '0 0 400px' }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Organisation Accounts Data
        </Typography>

        {!data || isLoading ? (
          <Typography variant="body1">Loading account data...</Typography>
        ) : parentOrgName ? (
          <Box sx={{ mb: 2 }} textAlign="center">
            <Typography variant="h6" mb={4} mt={6}>
              Account Paid for by {parentOrgName}
            </Typography>
            <Typography variant="body1" my={4}>
              If you need to update this account, please remove this
              organisation from the Umbrella Organisation.
            </Typography>
          </Box>
        ) : data.data ? (
          <Box sx={{ mb: 2 }}>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Membership Type
            </Typography>
            <Typography variant="body1" mb={1}>
              {data.data.membership || 'None'}
            </Typography>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Membership Until
            </Typography>
            <Typography variant="body1" mb={1}>
              {data.data.membership_until || 'N/A'}
            </Typography>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Tier
            </Typography>
            <Typography variant="body1" mb={1}>
              {getTierLabel(data.data.tier != null ? Number(data.data.tier) : null, 'None')}
            </Typography>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Payment Type
            </Typography>
            <Typography variant="body1" mb={1}>
              {data.data.payment_type || 'None'}
            </Typography>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Billing Frequency
            </Typography>
            <Typography variant="body1" mb={1}>
              {data.data.billing_frequency || 'None'}
            </Typography>
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Billing Amount
            </Typography>
            <Typography variant="body1" mb={1}>
              {isSponsored
                ? '$0 (Sponsored, not invoiced)'
                : data.data.billing_amount && Number(data.data.billing_amount) > 0
                  ? `$${data.data.billing_amount} (Custom)`
                  : getTierPricingLabel(data.data.tier != null ? Number(data.data.tier) : null)}
            </Typography>

            {/* Discount Information */}
            {orgWithDiscount &&
              orgWithDiscount.discount_amount &&
              orgWithDiscount.discount_amount > 0 && (
                <>
                  <Typography variant="body1" fontWeight={'bold'} mb={1}>
                    Active Discount
                  </Typography>
                  <Box sx={{ mb: 2 }}>
                    <Box
                      sx={{
                        p: 2,
                        bgcolor: 'success.50',
                        borderRadius: 1,
                        border: '1px solid',
                        borderColor: 'success.200',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                          mb: 1,
                        }}
                      >
                        <Chip
                          label={orgWithDiscount.discount_code}
                          size="small"
                          color="success"
                          variant="outlined"
                        />
                        <Typography
                          variant="body2"
                          color="success.main"
                          fontWeight={600}
                        >
                          -$
                          {orgWithDiscount.discount_amount?.toFixed(2) ||
                            '0.00'}
                        </Typography>
                      </Box>
                      <Typography variant="body2" color="text.secondary">
                        Expires:{' '}
                        {orgWithDiscount.expires_at
                          ? format(
                              new Date(orgWithDiscount.expires_at),
                              'MMM d, yyyy'
                            )
                          : 'No expiry date'}
                      </Typography>
                    </Box>
                  </Box>
                </>
              )}

            {/* Payment Connection Status */}
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Payment Connection
            </Typography>
            <Box sx={{ mb: 2 }}>
              {data.data.stripe_customer_id && (
                <Link
                  href={`https://dashboard.stripe.com/customers/${data.data.stripe_customer_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  View in Stripe
                  <LaunchIcon fontSize="small" />
                </Link>
              )}{' '}
              {data.data.xero_contact_id && (
                <Link
                  href={`https://go.xero.com/app/!r56tr/contacts/contact/${data.data.xero_contact_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  View in Xero
                  <LaunchIcon fontSize="small" />
                </Link>
              )}{' '}
              {!data.data.xero_contact_id && !data.data.stripe_customer_id && (
                <Typography variant="body1" color="textSecondary">
                  Not linked to payment provider
                </Typography>
              )}
              {data.data.xero_contact_id && (
                <Box sx={{ mt: 1 }}>
                  <Button
                    size="small"
                    variant="outlined"
                    startIcon={
                      syncingXeroBilling ? (
                        <CircularProgress size={14} />
                      ) : (
                        <SyncIcon fontSize="small" />
                      )
                    }
                    onClick={handleSyncXeroBilling}
                    disabled={syncingXeroBilling}
                  >
                    {syncingXeroBilling ? 'Syncing…' : 'Sync Billing'}
                  </Button>
                  {syncResult && (
                    <Alert
                      severity={syncResult.success ? 'success' : 'error'}
                      sx={{ mt: 1 }}
                      onClose={() => setSyncResult(null)}
                    >
                      {syncResult.message}
                    </Alert>
                  )}
                </Box>
              )}
            </Box>

            {/* Subscription Status */}
            <Typography variant="body1" fontWeight={'bold'} mb={1}>
              Subscription
            </Typography>
            <Box sx={{ mb: 2 }}>
              {data.data.stripe_subscription_id && (
                <Link
                  href={`https://dashboard.stripe.com/subscriptions/${data.data.stripe_subscription_id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {data.data.stripe_subscription_id ||
                    'View Stripe Subscription'}
                  <LaunchIcon fontSize="small" />
                </Link>
              )}{' '}
              {data.data.xero_repeating_invoice_id && (
                <Link
                  href={`https://go.xero.com/RepeatTransactions/Edit.aspx?invoiceID=${data.data.xero_repeating_invoice_id}&type=AR`}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.5,
                    textDecoration: 'none',
                    '&:hover': { textDecoration: 'underline' },
                  }}
                >
                  {data.data.repeating_invoice_template_name ||
                    'View Repeating Invoice'}
                  <LaunchIcon fontSize="small" />
                </Link>
              )}{' '}
              {isSponsored && !hasMissingSubscription && (
                <Alert severity="info">
                  Sponsored: the billing amount is $0, so no repeating invoice
                  is needed.
                </Alert>
              )}
              {hasMissingSubscription && (
                <>
                  <Alert
                    severity="warning"
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 1,
                      mb: xeroSetupResult ? 1 : 0,
                    }}
                  >
                    <WarningIcon />
                    <Box>
                      <Typography variant="body2">
                        No subscription found - manual setup required
                      </Typography>
                      {canSetupXeroInvoicing && (
                        <Button
                          variant="contained"
                          size="small"
                          onClick={handleSetupXeroInvoicing}
                          disabled={settingUpXeroInvoicing}
                          sx={{ mt: 1 }}
                        >
                          {settingUpXeroInvoicing ? (
                            <>
                              <CircularProgress size={16} sx={{ mr: 1 }} />
                              Setting up Xero...
                            </>
                          ) : (
                            'Set up Xero customer + invoicing'
                          )}
                        </Button>
                      )}
                    </Box>
                  </Alert>
                  {xeroSetupResult && (
                    <Alert
                      severity={xeroSetupResult.success ? 'success' : 'error'}
                    >
                      {xeroSetupResult.message}
                    </Alert>
                  )}
                </>
              )}
            </Box>

            {hasSubOrgs && orgChildrenData && (
              <>
                <Typography variant="body1" fontWeight={'bold'} mb={1}>
                  Child Orgs Paid for ({orgChildrenData?.data.length})
                </Typography>

                {orgChildrenData?.data.map((childOrg: any) => (
                  <Typography variant="body1" mb={1} key={childOrg.uuid}>
                    {childOrg.name}
                  </Typography>
                ))}
              </>
            )}
          </Box>
        ) : (
          <Typography variant="body1">No account data found.</Typography>
        )}

        {/* Edit Billing Button */}
        {!parentOrgName && (
          <Box sx={{ mt: 3 }}>
            <Button
              variant="contained"
              color="primary"
              onClick={() => handleOpenDialog()}
            >
              Edit Billing
            </Button>
          </Box>
        )}
      </Box>

      {/* Right column - Charges and Payments */}
      <Box sx={{ flex: 1 }}>
        <OrgChargesPayments orgUuid={orgUuid} />
      </Box>

      <Dialog
        open={isDialogOpen}
        onClose={handleCloseDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          {setupXeroAfterBillingSave
            ? 'Add Billing Details for Xero Setup'
            : 'Edit Org Billing Details'}
        </DialogTitle>
        <form onSubmit={handleSubmit(onSubmit)}>
          <DialogContent>
            {billingSaveError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {billingSaveError}
              </Alert>
            )}
            {setupXeroAfterBillingSave && (
              <Alert severity="info" sx={{ mb: 2 }}>
                Add the required billing details, then Xero customer and
                invoicing setup will continue automatically.
              </Alert>
            )}
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="membership"
                control={control}
                defaultValue={data?.data.membership || 'Free'}
                render={({ field }) => (
                  <TextField select label="Membership Type" {...field}>
                    <MenuItem value="Free">Free</MenuItem>
                    <MenuItem value="Paid">Paid</MenuItem>
                    <MenuItem value="Cancelled">Cancelled</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="payment_type"
                control={control}
                defaultValue={data?.data.payment_type || ''}
                render={({ field }) => (
                  <TextField select label="Payment Type" {...field}>
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Stripe" disabled={hasSubOrgs}>
                      Stripe
                    </MenuItem>
                    <MenuItem value="Invoice">Invoice</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="tier"
                control={control}
                defaultValue={data?.data.tier}
                render={({ field }) => (
                  <TextField select label="Tier" {...field}>
                    {watchPaymentType !== 'Stripe' && (
                      <MenuItem value="-1">Custom</MenuItem>
                    )}
                    <MenuItem value="0">Tier Micro</MenuItem>
                    <MenuItem value="1" disabled={hasSubOrgs}>
                      Tier 1
                    </MenuItem>
                    <MenuItem value="2" disabled={hasSubOrgs}>
                      Tier 2
                    </MenuItem>
                    <MenuItem value="3" disabled={hasSubOrgs}>
                      Tier 3
                    </MenuItem>
                    <MenuItem value="4" disabled={hasSubOrgs}>
                      Tier 4
                    </MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_frequency"
                control={control}
                defaultValue={data?.data.billing_frequency || ''}
                render={({ field }) => (
                  <TextField
                    select
                    label="Billing Frequency"
                    {...field}
                    disabled={watchPaymentType === 'Stripe' || saving}
                    value={field.value ?? ''}
                    onChange={(e) => field.onChange(e.target.value)}
                  >
                    <MenuItem value="">None</MenuItem>
                    <MenuItem value="Monthly">Monthly</MenuItem>
                    <MenuItem value="Quarterly">Quarterly</MenuItem>
                    <MenuItem value="Yearly">Yearly</MenuItem>
                  </TextField>
                )}
              />
            </FormControl>
            {/* Price */}
            {watchPaymentType !== 'Stripe' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Typography variant="subtitle2" sx={{ mb: 0.5 }}>
                  Price
                </Typography>
                <RadioGroup
                  value={pricing}
                  onChange={(e) => setPricing(e.target.value as Pricing)}
                >
                  <FormControlLabel
                    value="tier"
                    control={<Radio />}
                    label="Tier price"
                    disabled={String(watchTier) === '-1'}
                  />
                  <FormControlLabel
                    value="custom"
                    control={<Radio />}
                    label="Custom price"
                  />
                  <FormControlLabel
                    value="sponsored"
                    control={<Radio />}
                    label="Sponsored ($0, not invoiced)"
                  />
                </RadioGroup>
                <Typography variant="caption" color="text.secondary">
                  Saving a new price updates the Xero repeating invoice.
                  Sponsored removes it.
                </Typography>
              </FormControl>
            )}
            {pricing === 'custom' && watchPaymentType !== 'Stripe' && (
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="billing_amount"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      type="number"
                      label="Custom price per billing cycle (ex GST)"
                      inputProps={{ step: '0.01', min: '0.01' }}
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors.billing_amount}
                      helperText={
                        errors.billing_amount?.message ||
                        'Charged once per billing cycle: a yearly org pays this once a year'
                      }
                    />
                  )}
                />
              </FormControl>
            )}
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Billing Contact
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_legal_name"
                control={control}
                defaultValue={data?.data.billing_legal_name || ''}
                render={({ field }) => (
                  <TextField
                    label="Billing Legal Name"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_email"
                control={control}
                defaultValue={data?.data.billing_email || ''}
                rules={{ required: setupXeroAfterBillingSave }}
                render={({ field }) => (
                  <TextField
                    label="Billing Email"
                    type="email"
                    required={setupXeroAfterBillingSave}
                    {...field}
                    value={field.value ?? ''}
                    error={!!errors.billing_email}
                    helperText={errors.billing_email?.message}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_contact_name"
                control={control}
                defaultValue={data?.data.billing_contact_name || ''}
                render={({ field }) => (
                  <TextField
                    label="Billing Contact Name"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_contact_phone"
                control={control}
                defaultValue={data?.data.billing_contact_phone || ''}
                render={({ field }) => (
                  <TextField
                    label="Billing Contact Phone"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            <Typography variant="subtitle1" fontWeight={600} sx={{ mb: 1 }}>
              Billing Address
            </Typography>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_address_line1"
                control={control}
                defaultValue={data?.data.billing_address_line1 || ''}
                rules={{ required: setupXeroAfterBillingSave }}
                render={({ field }) => (
                  <TextField
                    label="Address Line 1"
                    required={setupXeroAfterBillingSave}
                    {...field}
                    value={field.value ?? ''}
                    error={!!errors.billing_address_line1}
                    helperText={errors.billing_address_line1?.message}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_address_line2"
                control={control}
                defaultValue={data?.data.billing_address_line2 || ''}
                render={({ field }) => (
                  <TextField
                    label="Address Line 2"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="billing_city"
                  control={control}
                  defaultValue={data?.data.billing_city || ''}
                  rules={{ required: setupXeroAfterBillingSave }}
                  render={({ field }) => (
                    <TextField
                      label="City"
                      required={setupXeroAfterBillingSave}
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors.billing_city}
                      helperText={errors.billing_city?.message}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="billing_postal_code"
                  control={control}
                  defaultValue={data?.data.billing_postal_code || ''}
                  rules={{ required: setupXeroAfterBillingSave }}
                  render={({ field }) => (
                    <TextField
                      label="Postal Code"
                      required={setupXeroAfterBillingSave}
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors.billing_postal_code}
                      helperText={errors.billing_postal_code?.message}
                    />
                  )}
                />
              </FormControl>
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="billing_state"
                  control={control}
                  defaultValue={data?.data.billing_state || ''}
                  render={({ field }) => (
                    <TextField
                      label="Region / State"
                      {...field}
                      value={field.value ?? ''}
                    />
                  )}
                />
              </FormControl>
              <FormControl fullWidth sx={{ mb: 2 }}>
                <Controller
                  name="billing_country"
                  control={control}
                  defaultValue={data?.data.billing_country || 'New Zealand'}
                  rules={{ required: setupXeroAfterBillingSave }}
                  render={({ field }) => (
                    <TextField
                      label="Country"
                      required={setupXeroAfterBillingSave}
                      {...field}
                      value={field.value ?? ''}
                      error={!!errors.billing_country}
                      helperText={errors.billing_country?.message}
                    />
                  )}
                />
              </FormControl>
            </Box>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="tax_registration_number"
                control={control}
                defaultValue={data?.data.tax_registration_number || ''}
                render={({ field }) => (
                  <TextField
                    label="Tax Registration Number"
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            <FormControl fullWidth sx={{ mb: 2 }}>
              <Controller
                name="billing_notes"
                control={control}
                defaultValue={data?.data.billing_notes || ''}
                render={({ field }) => (
                  <TextField
                    label="Billing Notes"
                    multiline
                    minRows={2}
                    {...field}
                    value={field.value ?? ''}
                  />
                )}
              />
            </FormControl>
            {hasSubOrgs &&
              orgChildrenData &&
              orgChildrenData?.data.length > 0 && (
                <>
                  <Typography variant="body1" fontWeight={'bold'} mb={1}>
                    Child Orgs this will pay for ({orgChildrenData?.data.length}
                    )
                  </Typography>
                  <Box
                    sx={{
                      mb: 2,
                      height: 180,
                      overflow: 'auto',
                      border: 1,
                      borderColor: '#ccc',
                      p: 2,
                      borderRadius: 1,
                    }}
                  >
                    {orgChildrenData?.data.map((childOrg: any) => (
                      <Typography variant="body1" mb={1} key={childOrg.uuid}>
                        {childOrg.name}
                      </Typography>
                    ))}
                  </Box>
                </>
              )}
          </DialogContent>
          <DialogActions sx={{ mb: 2, mr: 2 }}>
            <Button onClick={handleCloseDialog} type="button">
              Cancel
            </Button>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              disabled={saving}
            >
              {saving
                ? 'Saving...'
                : setupXeroAfterBillingSave
                  ? 'Save and Set Up Xero'
                  : 'Save'}
            </Button>
          </DialogActions>
        </form>
      </Dialog>
    </Box>
  )
}
